import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import ALink from 'tradera-components/alink';
import LoadingIcon from '@tradera/blueprint/theme/icons2/loading.svg?jsx';

const noop = () => {};

export const Button = props => {
    const {
        theme,
        size,
        children,
        link,
        disabled,
        unbutton,
        wide,
        round,
        translucent,
        className,
        branded,
        outline,
        fluid,
        click,
        iconHidden,
        iconFixed,
        href,
        tag,
        loading,
        toggle,
        toggleActive,
        hoverColor,
        input,
        nowrap,
        ...otherProps
    } = props;

    const allClasses = {
        color:
            theme && outline
                ? `btn-outline-${theme}`
                : theme
                ? `btn-${theme}`
                : '',
        hover: hoverColor && `btn-hover-${hoverColor}`,
        size: size && `btn-${size}`,
        reset: unbutton && 'unbutton',
        transparent: translucent && 'btn-translucent',
        link: link && 'btn-link',
        wide: wide && 'btn-wide',
        round: round && 'btn-round',
        fluid: fluid && 'btn-fluid',
        loading: loading && 'btn-loading',
        tag: tag && 'btn-tag',
        hiddenIcon: iconHidden && 'btn--icon-hidden',
        fixedIcon: iconFixed && 'btn--icon-fixed',
        nowrap: nowrap && 'text-nowrap text-truncate',
        toggle:
            toggle && toggleActive
                ? 'btn-toggle active'
                : toggle
                ? 'btn-toggle'
                : ''
    };

    const buttonClasses = ['btn', className, ...Object.values(allClasses)]
        .filter(Boolean)
        .join(' ');

    const onBtnClick = useCallback(
        event => {
            if (typeof click === 'function' && !loading) {
                click(event);
            }
        },
        [click, loading]
    );

    if (input) {
        return <input className={buttonClasses} {...otherProps} />;
    }

    return href ? (
        <ALink
            href={href}
            className={buttonClasses}
            onClick={onBtnClick}
            {...otherProps}>
            {children}
        </ALink>
    ) : (
        <button
            className={buttonClasses}
            onClick={onBtnClick}
            disabled={disabled}
            {...otherProps}>
            {children}
            {loading ? (
                <LoadingIcon className="icon icon-spin icon-after" />
            ) : null}
        </button>
    );
};

Button.propTypes = {
    theme: PropTypes.oneOf([
        'branded',
        'primary',
        'secondary',
        'tertiary',
        'success',
        'warning',
        'danger',
        'dark',
        'function',
        'info',
        'light',
        'white'
    ]),
    size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
    link: PropTypes.bool,
    disabled: PropTypes.bool,
    unbutton: PropTypes.bool,
    wide: PropTypes.bool,
    round: PropTypes.bool,
    translucent: PropTypes.bool,
    className: PropTypes.string,
    click: PropTypes.func,
    children: PropTypes.node,
    branded: PropTypes.bool,
    outline: PropTypes.bool,
    fluid: PropTypes.bool,
    iconHidden: PropTypes.bool,
    iconFixed: PropTypes.bool,
    href: PropTypes.string,
    tag: PropTypes.bool,
    loading: PropTypes.bool,
    toggle: PropTypes.bool,
    toggleActive: PropTypes.bool,
    hoverColor: PropTypes.string
};

Button.defaultProps = {
    click: noop,
    disabled: false
};
